/**
 * API functions for Tour Guide AI.
 */

const DISABLE_API = false;

/**
 * Fetches places that match a given set of keywords using the keyword-based endpoint.
 *
 * @param {string} communityNid - The Node ID of the community.
 * @param {Array<string>} keywords - An array of keywords to search for.
 * @returns {Promise<object>} - A promise that resolves to the matching places.
 */
export const getPlacesByKeywords = async (communityNid, keywords) => {
  if (!keywords || keywords.length === 0) {
    return { places: [], total_found: 0 };
  }
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const params = new URLSearchParams({
    community_nid: communityNid,
    keywords: keywords.join(','),
  });
  const url = `${apiUrl}/your-tour-chatbot/places-by-keywords?${params.toString()}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[TourGuideAi] Error response from Drupal:', response.status, response.statusText);
      return { places: [], total_found: 0 };
    }
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('[TourGuideAi] Exception during fetch:', err);
    return { places: [], total_found: 0 };
  }
};

/**
 * Fetches keywords from the keywords taxonomy using the custom chatbot endpoint.
 *
 * @param {number} limit - Number of keywords to fetch (default 10, max 50).
 * @returns {Promise<object>} - A promise that resolves to the keywords data.
 */
export const getKeywords = async (limit = 10) => {
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const params = new URLSearchParams({
    limit: Math.min(Math.max(limit, 1), 50), // Ensure limit is between 1-50
  });
  const url = `${apiUrl}/your-tour-chatbot/keywords?${params.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[TourGuideAi] Error response from Drupal:', response.status, response.statusText);
      return { keywords: [], total_available: 0 };
    }
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('[TourGuideAi] Exception during keywords fetch:', err);
    return { keywords: [], total_available: 0 };
  }
};

/**
 * Sends a user question to the AI model for processing.
 *
 * @param {string} question - The user's question.
 * @param {object} context - The conversation context including user info and interests.
 * @returns {Promise<object>} - A promise that resolves to the AI response.
 */
/**
 * Creates a new chat session in Drupal.
 */
export const createChatSession = async (sessionData) => {
  if (DISABLE_API) return { success: true };
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const url = `${apiUrl}/your-tour-chatbot/session/create`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionData),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('[TourGuideAi] Failed to create session:', data);
      return { success: false, error: data.message || 'Failed to create session' };
    }

    return data;
  } catch (err) {
    console.error('[TourGuideAi] Exception creating session:', err);
    return { success: false, error: 'Network error creating session' };
  }
};

/**
 * Completes a chat session in Drupal.
 */
export const completeChatSession = async (sessionId, completionType = 'natural') => {
  if (DISABLE_API) return { success: true };
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const url = `${apiUrl}/your-tour-chatbot/session/complete`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, completionType }),
      // keepalive lets this request finish even when fired from a page-exit
      // handler (the abandon beacon), where the page is being torn down.
      keepalive: true,
    });

    const data = await response.json();

    if (!response.ok) {
      console.warn('[TourGuideAi] Failed to complete session:', data);
      return { success: false, error: data.message || 'Failed to complete session' };
    }

    return data;
  } catch (err) {
    console.warn('[TourGuideAi] Exception completing session:', err);
    return { success: false, error: 'Network error completing session' };
  }
};

/**
 * Logs a synthesized bot prompt to Airtable (fire-and-forget).
 *
 * Used for bot messages generated client-side that never round-trip
 * through askAIQuestion (e.g. the follow-up prompts after the
 * Ask a Question / share another interest buttons).
 */
export const logBotMessage = (message, context = {}, sender = 'bot') => {
  if (DISABLE_API || !message) return;
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const url = `${apiUrl}/your-tour-api/log-bot-message`;

  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message, context, sender, clientTimestamp: new Date().toISOString(),
      }),
      keepalive: true,
    }).catch((err) => {
      console.warn('[TourGuideAi] Failed to log bot message:', err);
    });
  } catch (err) {
    console.warn('[TourGuideAi] Exception logging bot message:', err);
  }
};

export const getInterestCategories = async () => {
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const url = `${apiUrl}/your-tour-chatbot/interest-categories`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('[TourGuideAi] Error fetching interest categories:', response.status);
      return { categories: [] };
    }
    return await response.json();
  } catch (err) {
    console.error('[TourGuideAi] Exception fetching interest categories:', err);
    return { categories: [] };
  }
};

export const askAIQuestion = async (question, context = {}) => {
  if (!question || question.trim() === '') {
    return { response: 'Please provide a question for me to answer.' };
  }

  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  const url = `${apiUrl}/your-tour-api/chatbot-question`;

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      question: question.trim(),
      context: JSON.stringify(context),
      // Same clock as the clicks logBotMessage sends, so the transcript
      // orders the question among them even when the browser clock is off.
      clientTimestamp: new Date().toISOString(),
    }),
  };

  try {
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      console.error('[TourGuideAi] Error response from AI:', response.status, response.statusText);
      return { response: 'I apologize, but I\'m having trouble processing your question right now. Please try again later or speak with us for assistance.' };
    }

    const data = await response.json();
    return data;
  } catch (err) {
    console.error('[TourGuideAi] Exception during AI question:', err);
    return { response: 'I apologize, but I\'m having trouble processing your question right now. Please try again later or speak with us for assistance.' };
  }
};
